
import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, arrayUnion, onSnapshot } from 'firebase/firestore';
import { getToken } from 'firebase/messaging';
import { UserProfile, SchoolProfile } from '../types';

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  schoolProfile: SchoolProfile | null;
  loading: boolean;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [schoolProfile, setSchoolProfile] = useState<SchoolProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async (uid: string) => {
    try {
      const userDocRef = doc(db, 'users', uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const userData = userDocSnap.data() as UserProfile;

        // Fetch Stripe Subscription
        const subsRef = collection(db, 'customers', uid, 'subscriptions');
        const subsQuery = query(subsRef, where('status', 'in', ['trialing', 'active']));
        const subsSnap = await getDocs(subsQuery);

        const hasActiveStripeSubscription = !subsSnap.empty;

        // Merge real subscription status into the profile for the frontend
        const enrichedProfile: UserProfile = {
          ...userData,
          subscriptionStatus: hasActiveStripeSubscription ? 'active' : userData.subscriptionStatus,
          isPaid: hasActiveStripeSubscription || userData.isPaid,
        };

        setUserProfile(enrichedProfile);

        // --- Push Notifications (FCM) Logic ---
        // We do this asynchronously so it doesn't block the UI loading
        setupNotifications(uid, userDocRef);

        if (userData.schoolId && userData.role !== 'parent') {
          const schoolDocRef = doc(db, 'schools', userData.schoolId);
          const schoolDocSnap = await getDoc(schoolDocRef);
          if (schoolDocSnap.exists()) {
            setSchoolProfile(schoolDocSnap.data() as SchoolProfile);
          } else {
            setSchoolProfile(null);
          }
        } else {
          setSchoolProfile(null);
        }
      } else {
        setUserProfile(null);
        setSchoolProfile(null);
      }
    } catch (error: any) {
      console.log("Profile fetch error:", error);
    }
  };

  const setupNotifications = async (uid: string, userDocRef: any) => {
    try {
        // Dynamically import messaging so we don't break SSR or unsupported environments
        const { messaging } = await import('../firebase');
        if (!messaging) return; // Not supported

        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
            const currentToken = await getToken(messaging, { 
                vapidKey: 'BHKCJNHc_NPJXtPde6HE0PdkFqc_opai08PLuL-2-UK0HyBMHQXQOWMzrkEhEtA0A5Ujqo9pNc8GQuOUfZ1PagE'
            }).catch(err => {
                console.log('An error occurred while retrieving token. ', err);
                return null;
            });

            if (currentToken) {
                // Save the token to the user's document using arrayUnion so they can have multiple devices
                await updateDoc(userDocRef, {
                    fcmTokens: arrayUnion(currentToken)
                });
            }
        }
    } catch (error) {
        console.log("Error setting up notifications:", error);
    }
  };

  useEffect(() => {
    let unsubProfile: (() => void) | undefined;

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await fetchData(user.uid);
        
        // Attach real-time listener for remote admin changes
        unsubProfile = onSnapshot(doc(db, 'users', user.uid), (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data() as UserProfile;
                setUserProfile(prev => {
                    if (!prev) return data;
                    // Overlay new firestore data but preserve the Stripe enrichments from fetchData
                    return {
                        ...data,
                        subscriptionStatus: prev.subscriptionStatus,
                        isPaid: prev.isPaid
                    };
                });
            }
        });
      } else {
        if (unsubProfile) unsubProfile();
        setUserProfile(null);
        setSchoolProfile(null);
      }
      setLoading(false);
    });

    return () => {
      unsubscribeAuth();
      if (unsubProfile) unsubProfile();
    };
  }, []);

  const refreshProfile = async () => {
    if (currentUser) {
      await fetchData(currentUser.uid);
    }
  };

  return (
    <AuthContext.Provider value={{ currentUser, userProfile, schoolProfile, loading, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
};
